import { Component, OnInit } from '@angular/core';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { FormComponentLookup, FormNavigationConfig, DefaultFormConfig, FormPageComponentClass } from '@ptl/forms';
import { ContentStorageService, PtlNotificationService, PtlNotificationStyle, PtlWindowLocationService } from '@ptl/global-components';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { UserInfoService } from '../../../shared/services/user-info.service';
import { PurlFormState, SendReferralFormState } from '../interfaces/purl.interface';
import { Email } from '../../../producer/interfaces/email.interface';
import { patchSendReferralState, PURL_STATE } from '../reducer/purl.reducer';
import { PtlSessionService } from '../../../shared/components/ptl-session-loader/services/ptl-session.service';
import { PlansStateStructure } from '../../../plan-selection/configs/interfaces/plansStateStructure.interface';
import { ZipCountyStateStructure } from '../../../shared/interfaces/zipCountyStateStructure.interface';

@FormComponentLookup('page-send-referral')
@Component({
    selector: 'app-page-send-referral',
    templateUrl: './page-send-referral.component.html',
    styleUrls: ['./page-send-referral.component.scss'],
    standalone: false
})
export class PageSendReferralComponent extends FormPageComponentClass<FormNavigationConfig> implements OnInit {

  PLAN_OPTIONS_TYPE = 'PLANOPTIONS';
  EMAIL_REFERRAL_TYPE = 'EMAILREFERRAL';
  saveMedicalDetail = false;
  isLoading = false;

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private userInfoService: UserInfoService,
    private ptlNotificationService: PtlNotificationService,
    private windowService: PtlWindowLocationService,
    private ptlSessionService: PtlSessionService,
    private contentStorage: ContentStorageService
  ) {
    super();
  }

  ngOnInit() {
    // Need to prepopulate the form if we are coming from save detail page
    if (this.params.get('saveDetail') === 'true') {
      this.configurePageFromPlanDetail();
    }

    this.initPageConfig();
  }

  submitHook(formConfig) {
    return new Promise((resolve, reject) => {
      this.isLoading = true;

      this.userInfoService.sendReferralEmail(this.getPurlPayload()).then((response) => {
          this.ptlNotificationService.open({
            title: 'Success',
            content: 'Your email was sent!' + (this.saveMedicalDetail ? ' You will now be taken back to the plan details page.' : ''),
            primaryButtonText: 'OK',
            notificationStyle: PtlNotificationStyle.SUCCESS
          })
          .afterClosed().subscribe(() => {
            if (this.saveMedicalDetail) {
              let content = this.contentStorage.getContentMap('enrollment');

              if (content.get('planDetailsUrl')) {
                this.windowService.goToUrl(content.get('planDetailsUrl'));
              }
              else {
                // Fallback, plan enrollment form 'adds' extra page change when adding the formPage query param
                history.back();
                history.back();
              }
            }
          });

          this.resetFields();

          resolve(true);
      })
      .catch((error) => {
        this.ptlNotificationService.open({
          title: 'Error Sending Email',
          content: 'A network error occurred. Please try again later or contact support.',
          primaryButtonText: 'OK',
          notificationStyle: PtlNotificationStyle.DANGER
        });
      })
      .finally(() => {
        this.isLoading = false
      });
    });
  }

  getPurlPayload(): Email {
    const sendReferralState: SendReferralFormState = (this.reducerUtilityService.getPayloadState(PURL_STATE) as PurlFormState).sendReferral;
    let payload = this.getBasePurlPayload(sendReferralState);

    // Add the extra connecture parameters if needed
    if (sendReferralState.connectureUrl) {
      payload.etAttributes.push({
        name: "Variable_Link",
        value: sendReferralState.connectureUrl
      });
    }
    
    // Add extra attributes for saving the session if there is a plan selected
    let plan = sendReferralState.planSelection;
    if (plan) {
      const capturedState = this.ptlSessionService.captureSessionFromState([plan.productId], sendReferralState.productGroup, sendReferralState.year);

      payload.etAttributes.push({
        name: 'planSessionPayload',
        value: JSON.stringify(capturedState)
      });
      payload.etAttributes.push({
        name: 'Plan_Name',
        value: plan.productName
      });
    }

    return payload;
  }

  showGenericError() {
    this.ptlNotificationService.open({
      title: 'Error',
      content: 'Something went wrong. Please try again later.',
      primaryButtonText: 'OK',
      notificationStyle: PtlNotificationStyle.DANGER
    });
  }

  resetFields() {
    const sendReferralState: SendReferralFormState = (this.reducerUtilityService.getPayloadState(PURL_STATE) as PurlFormState).sendReferral;

    // TODO - maybe have someway to configure this type of functionality within the forms
    let savedPartialState: SendReferralFormState = {
      selectedAgency: sendReferralState.selectedAgency,
      agencyNpn: sendReferralState.agencyNpn,
      agencyNumber: sendReferralState.agencyNumber,
      zipCode: sendReferralState.zipCode,
      county: sendReferralState.county,
      state: sendReferralState.state
    } as SendReferralFormState;
          
    this.pageFormGroups['purl.sendReferral'].reset();
    
    setTimeout(() => {
      this.ngRedux.dispatch(patchSendReferralState(savedPartialState));
    }, 100)
  }

  configurePageFromPlanDetail() {
    let patchValues = {} as SendReferralFormState;
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getComponentStates().ZipCounty;
    const capturedState = this.ptlSessionService.captureSessionFromState(null, null, null);

    let refTypeControl = this.formConfig.pages[0].entries.filter((entry) => entry?.formControlAttributes?.formControlName === 'referralType')[0];
    let productGroupControl = this.formConfig.pages[0].entries.filter((entry) => entry?.formControlAttributes?.formControlName === 'productGroup')[0];
    let planSelectionControl = this.formConfig.pages[0].entries.filter((entry) => entry?.formControlAttributes?.formControlName === 'planSelection')[0];
    let zipCodeControl = this.formConfig.pages[0].entries.filter((entry) => entry?.formControlAttributes?.formControlName === 'zipCode')[0];
    let yearControl = this.formConfig.pages[0].entries.filter((entry) => entry?.formControlAttributes?.formControlName === 'year')[0];
    
    // Patch values from selected plan to purl state
    patchValues.productGroup = planState.activeProductGroup;
    patchValues.planSelection = planState.medicareAdvantage_or_PDP_planForDRX;
    patchValues.year = planState.medicareAdvantage_or_PDP_planForDRX.year?.toString();
    patchValues.zipCode = zipCountyState.zipCode;
    patchValues.county = zipCountyState.county;
    patchValues.state = zipCountyState.county.state;

    // Default this to show so the plan selection doesn't get wiped out due to form listener activity
    planSelectionControl.base.display = true;

    // Plan Options flow only applies if we have used the cost estimator in any capacity
    if (capturedState.medicationInformation.currentEncodedMeds !== null ||
        capturedState.pharmacyState.pharmacys?.length ||
        capturedState.doctorState.doctors?.length) {

      // Indicates we are saving a full detail session to alter post-email process
      this.saveMedicalDetail = true;

      // Add a 'plan options' option to the referral type drop down
      patchValues.referralType = this.PLAN_OPTIONS_TYPE;
      refTypeControl.selectAttributes.optionAttributes.push({label: 'Plan Options', value: this.PLAN_OPTIONS_TYPE});

      // Disable the prepopulated fields
      refTypeControl.formControlAttributes.disabled = true;
      productGroupControl.formControlAttributes.disabled = true;
      planSelectionControl.formControlAttributes.disabled = true;
      zipCodeControl.formControlAttributes.disabled = true;
      yearControl.formControlAttributes.disabled = true;
    }
    else {
      patchValues.referralType = this.EMAIL_REFERRAL_TYPE;
    }

    this.ngRedux.dispatch(patchSendReferralState(patchValues));
  }

  getBasePurlPayload(sendReferralState: SendReferralFormState) {
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    return {
      member: false,
      deliveryRecipient: sendReferralState.customerEmailAddress,
      deliveryMethod: 'Email',
      templateName: 'ProducerSharedMedicareBlank',
      memberProfileId: '',
      groupNumber: '',
      umi: '',
      firstName: '',
      lastName: '',
      dob: '',
      etAttributes: [
        {
          name: 'agentCode',
          value: sendReferralState.agencyNpn
        },
        {
          name: 'agencyNumber',
          value: sendReferralState.agencyNumber
        },
        {
          name: 'Company',
          value: sendReferralState.selectedAgency.agencyName
        },
        {
          name: 'Brand_Name',
          value: sendReferralState.county.region
        },
        {
          name: 'County',
          value: sendReferralState.county.county
        },
        {
          name: 'user_ID',
          value: loginInfo.userId
        },
        {
          name: 'EmailTypePreference',
          value: sendReferralState.referralType
        },
        {
          name: 'year',
          value: sendReferralState.year
        },
        {
          name: 'Agent_Name',
          value: loginInfo.userInfo.firstName + ' ' + loginInfo.userInfo.lastName
        },
        {
          name: 'Agent_Phone',
          value: loginInfo.userInfo.telephone
        },
        {
          name: 'clientName',
          value: sendReferralState.clientName?.trim() ? sendReferralState.clientName.trim() : ''  // must be empty string, not null
        },
        {
          name: 'agentNotes',
          value: sendReferralState.agentNotes?.trim() ? sendReferralState.agentNotes.trim() : ''  // must be empty string, not null
        }
      ]
    };
  }
}
